import React, { type FormEvent } from 'react';
import { styled } from '@compiled/react';
import { ErrorMessage } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	PRIORITY_SCHEME_MODAL_DESC_FIELD_VALIDATION_ERROR_SELECTOR,
	PRIORITY_SCHEME_MODAL_NAME_AND_DESCRIPTION_FIELDS_SELECTOR,
	PRIORITY_SCHEME_MODAL_NAME_FIELD_VALIDATION_ERROR_SELECTOR,
} from '../../../../common/constants';
import {
	PRIORITY_SCHEME_DESCRIPTION_FIELD,
	PRIORITY_SCHEME_NAME_FIELD,
	MAX_NAME_LENGTH,
	MAX_DESCRIPTION_LENGTH,
} from '../../../../common/ui/constants';
import type { CreateOrEditPriorityScheme } from '../../../../common/ui/types';
import messages from './messages';

export type NameAndDescriptionViewProps = {
	/**
	 * State which will be used to call APIs
	 */
	priorityScheme: CreateOrEditPriorityScheme;
	setPriorityScheme: (priorityScheme: CreateOrEditPriorityScheme) => void;
	nameError: string;
	descriptionError: string;
	/**
	 * Will default to false unless specified
	 */
	isDisabled?: boolean;
};

export const NameAndDescriptionView = ({
	priorityScheme,
	setPriorityScheme,
	nameError,
	descriptionError,
	isDisabled = false,
}: NameAndDescriptionViewProps) => {
	const isDefaultScheme = priorityScheme.isDefault ?? false;
	const { formatMessage } = useIntl();

	const nameFieldIsInvalid: boolean = nameError !== '';
	const descriptionFieldIsInvalid: boolean = descriptionError !== '';

	const onChangeNameField = (e: FormEvent<HTMLInputElement>) => {
		const nameFieldValue: string = e.currentTarget.value;
		setPriorityScheme({
			...priorityScheme,
			name: nameFieldValue,
		});
	};

	const onChangeDescriptionField = (e: FormEvent<HTMLInputElement>) => {
		const descriptionFieldValue: string = e.currentTarget.value;
		setPriorityScheme({
			...priorityScheme,
			description: descriptionFieldValue,
		});
	};

	return (
		<NameAndDescriptionWrapper>
			<FieldWrapper>
				<FieldTitleWrapper>
					<Heading level="h200">
						{formatMessage(messages.prioritySchemeNameFieldTitle)}
						<AsteriskWrapper>*</AsteriskWrapper>
					</Heading>
				</FieldTitleWrapper>
				<TextField
					testId={PRIORITY_SCHEME_MODAL_NAME_AND_DESCRIPTION_FIELDS_SELECTOR}
					name={PRIORITY_SCHEME_NAME_FIELD}
					label={formatMessage(messages.prioritySchemeNameFieldTitle)}
					isRequired
					isDisabled={isDefaultScheme || isDisabled}
					defaultValue={priorityScheme.name ?? ''}
					placeholder={formatMessage(messages.prioritySchemeNameFieldPlaceholder)}
					maxLength={MAX_NAME_LENGTH}
					isInvalid={nameFieldIsInvalid}
					onChange={onChangeNameField}
				/>
				{nameFieldIsInvalid && (
					<>
						<ErrorMessage testId={PRIORITY_SCHEME_MODAL_NAME_FIELD_VALIDATION_ERROR_SELECTOR}>
							<div aria-live="polite">{nameError}</div>
						</ErrorMessage>
					</>
				)}
			</FieldWrapper>

			<FieldWrapper>
				<FieldTitleWrapper>
					<Heading level="h200">
						{formatMessage(messages.prioritySchemeDescriptionFieldTitle)}
					</Heading>
				</FieldTitleWrapper>
				<TextField
					testId={PRIORITY_SCHEME_MODAL_NAME_AND_DESCRIPTION_FIELDS_SELECTOR}
					name={PRIORITY_SCHEME_DESCRIPTION_FIELD}
					label={formatMessage(messages.prioritySchemeDescriptionFieldTitle)}
					isDisabled={isDefaultScheme || isDisabled}
					defaultValue={priorityScheme.description ?? ''}
					placeholder={formatMessage(messages.prioritySchemeDescriptionFieldPlaceholder)}
					maxLength={MAX_DESCRIPTION_LENGTH}
					isInvalid={descriptionFieldIsInvalid}
					onChange={onChangeDescriptionField}
				/>
				{descriptionFieldIsInvalid && (
					<ErrorMessage testId={PRIORITY_SCHEME_MODAL_DESC_FIELD_VALIDATION_ERROR_SELECTOR}>
						<div aria-live="polite">{descriptionError}</div>
					</ErrorMessage>
				)}
			</FieldWrapper>
		</NameAndDescriptionWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled.div({
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldTitleWrapper = styled.div({
	marginBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AsteriskWrapper = styled.span({
	paddingLeft: token('space.025', '2px'),
	color: token('color.text.danger', '#DE350B'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameAndDescriptionWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});
